export const AUTOMATIONS_MODES = [
  { value: 'draft_only', label: 'Draft only' },
  { value: 'approval_required', label: 'Approval required' },
  { value: 'guarded_autopilot', label: 'Guarded autopilot' },
];

export const GENERATION_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Selected weekdays' },
  { value: 'interval', label: 'Interval (hours)' },
];

export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export const RUN_ONCE_CREDIT_WARNING =
  'Run Once may call OpenAI and spend AI credits when configured. Continue?';

export const GENERATION_SCHEDULE_DISCLAIMER =
  'Schedule generation only decides when AI creates drafts. It never authorizes LinkedIn publishing by itself.';

export const PUBLISHING_MODE_HINT =
  'Publishing mode controls what happens after a draft exists. Approval required: review then Publish Now or Schedule post on Draft Posts. Guarded autopilot may publish only after quality, identity, cap, and pause checks pass.';

export const SUPPORTED_TASK_ACTIONS = ['health_check'];

export function identityLabel(identities, identityId) {
  if (!identityId) return 'No LinkedIn identity selected';
  const found = (identities || []).find((item) => item.id === identityId);
  if (!found) return `Identity ${identityId}`;
  return `${found.name} (${found.type})`;
}

export function modeLabel(mode) {
  return AUTOMATIONS_MODES.find((m) => m.value === mode)?.label || mode;
}

export function hasGenerationSchedule(pb) {
  return !!(pb?.has_generation_schedule || pb?.generation_frequency);
}

export function formatPlaybookLocal(isoOrDate, timeZone) {
  if (!isoOrDate) return '—';
  try {
    const d = typeof isoOrDate === 'string' || typeof isoOrDate === 'number'
      ? new Date(isoOrDate)
      : isoOrDate;
    if (Number.isNaN(d.getTime())) return '—';
    const tz = timeZone || 'UTC';
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(d);
  } catch {
    return String(isoOrDate);
  }
}

export function scheduleFormFromPlaybook(pb = {}) {
  return {
    enabled: pb.generation_schedule_enabled !== false,
    frequency: pb.generation_frequency || 'daily',
    local_time: pb.generation_local_time || '09:00',
    weekdays: Array.isArray(pb.generation_weekdays) && pb.generation_weekdays.length
      ? pb.generation_weekdays.map(Number)
      : [0, 1, 2, 3, 4],
    interval_hours: pb.generation_interval_hours || 24,
  };
}

export function schedulePayloadFromForm(form) {
  const payload = {
    enabled: !!form.enabled,
    frequency: form.frequency,
  };
  if (form.frequency === 'interval') {
    payload.interval_hours = Number(form.interval_hours) || 24;
  } else {
    payload.local_time = form.local_time || '09:00';
  }
  if (form.frequency === 'weekdays') {
    payload.weekdays = (form.weekdays || []).map(Number);
  }
  return payload;
}

export function playbookFormFromRecord(pb = {}, identities = []) {
  return {
    name: pb.name || '',
    product_service: pb.product_service || '',
    business_objective: pb.business_objective || '',
    target_audience: pb.target_audience || '',
    approved_cta: pb.approved_cta || '',
    allowlisted_urls: Array.isArray(pb.allowlisted_urls) ? pb.allowlisted_urls.join('\n') : '',
    source_app_id: pb.source_app_id || '',
    source_urls: Array.isArray(pb.source_urls) ? pb.source_urls.join('\n') : '',
    prohibited_claims: Array.isArray(pb.prohibited_claims) ? pb.prohibited_claims.join('\n') : '',
    approved_facts: Array.isArray(pb.approved_facts) ? pb.approved_facts.join('\n') : '',
    content_pillars: Array.isArray(pb.content_pillars)
      ? pb.content_pillars.map((p) => (typeof p === 'string' ? p : p?.name || '')).filter(Boolean).join('\n')
      : '',
    tone_rules: pb.tone_rules || '',
    mode: pb.mode || 'approval_required',
    quality_threshold: pb.quality_threshold ?? 70,
    max_generations_per_day: pb.max_generations_per_day ?? 5,
    max_publishes_per_day: pb.max_publishes_per_day ?? 1,
    picture_required: !!pb.picture_required,
    publishing_identity_id: pb.publishing_identity_id || identities?.[0]?.id || '',
    timezone: pb.timezone || 'UTC',
    autopilot_publish_local_time: pb.autopilot_publish_local_time || '',
    autopilot_publish_window_start: pb.autopilot_publish_window_start || '',
    autopilot_publish_window_end: pb.autopilot_publish_window_end || '',
  };
}

function _hhmmToMinutes(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const parts = text.split(':');
  if (parts.length !== 2) return NaN;
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return NaN;
  }
  return hour * 60 + minute;
}

/** Validate guarded-autopilot publish timing. Returns null when valid, else an error string. */
export function validateAutopilotPublishConfig({
  autopilot_publish_local_time: localTime,
  autopilot_publish_window_start: windowStart,
  autopilot_publish_window_end: windowEnd,
} = {}) {
  const local = (localTime || '').trim();
  const start = (windowStart || '').trim();
  const end = (windowEnd || '').trim();
  if (!local && !start && !end) return null;

  const localM = local ? _hhmmToMinutes(local) : null;
  const startM = start ? _hhmmToMinutes(start) : null;
  const endM = end ? _hhmmToMinutes(end) : null;
  if ([localM, startM, endM].some((v) => Number.isNaN(v))) {
    return 'Autopilot publish times must be valid HH:MM values';
  }
  if (Boolean(start) !== Boolean(end)) {
    return 'Autopilot window start and end must both be set';
  }
  if (start && end) {
    if (startM > endM) {
      return 'Overnight autopilot windows are not supported (start must be <= end)';
    }
    if (local) {
      if (localM < startM || localM > endM) {
        return 'Exact autopilot publish time must fall within the publish window';
      }
    }
    return null;
  }
  if (local) return null;
  return 'Invalid autopilot publish configuration';
}

export function resolveAutopilotPublishSummary(pb) {
  const local = (pb?.autopilot_publish_local_time || '').trim();
  const start = (pb?.autopilot_publish_window_start || '').trim();
  const end = (pb?.autopilot_publish_window_end || '').trim();
  if (!local && !start && !end) return 'not configured';
  if (local && start && end) return `${local} (within ${start}–${end})`;
  if (!local && start && end) return `${start}–${end} (publishes at ${start})`;
  if (local) return local;
  return 'invalid';
}

export function playbookPayloadFromForm(form) {
  const autopilotError = validateAutopilotPublishConfig(form);
  if (autopilotError) {
    const err = new Error(autopilotError);
    err.code = 'AUTOPILOT_WINDOW_INVALID';
    throw err;
  }
  return {
    name: form.name.trim(),
    product_service: form.product_service || null,
    business_objective: form.business_objective || null,
    target_audience: form.target_audience || null,
    approved_cta: form.approved_cta || null,
    tone_rules: form.tone_rules || null,
    mode: form.mode,
    quality_threshold: Number(form.quality_threshold) || 70,
    max_generations_per_day: Number(form.max_generations_per_day) || 5,
    max_publishes_per_day: Number(form.max_publishes_per_day) || 1,
    picture_required: !!form.picture_required,
    platform: 'linkedin',
    timezone: form.timezone || 'UTC',
    publishing_identity_id: form.publishing_identity_id || null,
    source_app_id: form.source_app_id || null,
    source_urls: form.source_app_id && form.source_urls
      ? form.source_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
      : [],
    autopilot_publish_local_time: form.autopilot_publish_local_time || null,
    autopilot_publish_window_start: form.autopilot_publish_window_start || null,
    autopilot_publish_window_end: form.autopilot_publish_window_end || null,
    allowlisted_urls: form.allowlisted_urls
      ? form.allowlisted_urls.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
      : [],
    prohibited_claims: form.prohibited_claims
      ? form.prohibited_claims.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
      : [],
    approved_facts: form.approved_facts
      ? form.approved_facts.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
      : [],
    content_pillars: form.content_pillars
      ? form.content_pillars.split(/[\n,]/).map((s) => s.trim()).filter(Boolean).map((name) => ({ name }))
      : [],
  };
}
